/**
 * Cart API
 * -----------------------------------------------------------------------------
 * By default, all cart manipulations that use the Cart Ajax API should be done through these methods.
 * All methods have `Request` at the end of the method name.
 * All methods return the awaited response from the API call.
 * Methods that manipulate the cart can be passed a sections array to request bundled section rendering in the response.
 * All request URLs are localised
 *
 * See https://shopify.dev/docs/api/ajax/reference/cart
 */

// Shared variables
const defaultSectionsUrl =
  globalThis.location.pathname + globalThis.location.search;
const rootUrl = globalThis.Shopify.routes.root;

/**
 * Fetch the cart
 * @returns {Promise.<Response>} - The cart response
 */
const fetchCartRequest = async () => {
  const response = await fetch(`${rootUrl}cart.js`);
  return response;
};

/**
 * Change the quantity of an item in the cart by variant ID
 *
 * @param {String} id either the variant ID or the key of the line item in the cart
 * @param {Number} quantity
 * @param {Array.<String>} sections (optional)
 * @param {String} sections_url - should be a relative path, beginning with '/' (optional)
 * @returns {Promise.<Response>}
 */
const changeItemQuantityRequest = async (
  id,
  quantity,
  sections = [],
  sections_url = defaultSectionsUrl
) => {
  const response = await fetch(`${rootUrl}cart/change.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      id,
      quantity,
      sections,
      sections_url
    })
  });
  return response;
};

/**
 * Set the quantity of each line item passed to zero
 *
 * @param {Array.<Number>} items - An array of either variant IDs or line item keys
 * @param {Array.<String>} sections (optional)
 * @param {String} sections_url - should be a relative path, beginning with '/' (optional)
 * @returns {Promise.<Response>}
 */
const removeLineItemsRequest = async (
  itemIds,
  sections = [],
  sections_url = defaultSectionsUrl
) => {
  const updates = {};
  for (const id of itemIds) {
    updates[id] = 0;
  }

  const response = await fetch(`${rootUrl}cart/update.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      updates,
      sections,
      sections_url
    })
  });
  return response;
};

/**
 * Add items to the cart
 *
 * @param {Array.<object>} items
 * @param {String} items.id
 * @param {Number} items.quantity
 * @param {Array.<String>} sections (optional)
 * @param {String} sections_url - should be a relative path, beginning with '/' (optional)
 * @returns {Promise.<Response>}
 */
const addItemsToCartRequest = async (
  items,
  sections = [],
  sections_url = defaultSectionsUrl
) => {
  const response = await fetch(`${rootUrl}cart/add.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      items,
      sections,
      sections_url
    })
  });
  return response;
};

export {
  addItemsToCartRequest,
  changeItemQuantityRequest,
  fetchCartRequest,
  removeLineItemsRequest as removeVariantsRequest
};
